import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { injectMutation } from '@tanstack/angular-query-experimental';
import {
  clearToastAction,
  errorToastAction,
  loadingToastAction,
} from '../../../core/store/actions/toast.actions';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { PasswordInputComponent } from '../../../shared/widgets/inputs/password-input/password-input.component';
import { GeneralInputComponent } from '../../../shared/widgets/inputs/general-input/general-input.component';
import { GeneralButtonDirective } from '../../../shared/directives/buttons/general-button.directive';
import { SvgIconComponent } from 'angular-svg-icon';
import { RedirectService } from '../../../core/services/redirect.service';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-admin-login',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    PasswordInputComponent,
    GeneralInputComponent,
    ReactiveFormsModule,
    RouterModule,
    GeneralButtonDirective,
    SvgIconComponent,
  ],
  templateUrl: './admin-login.component.html',
  styleUrl: './admin-login.component.css',
})
export class AdminLoginComponent {
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  redirectService = inject(RedirectService);
  store = inject(Store<AppState>);
  routeTo = injectQueryParams('routeTo');
  reCaptchaV3Service = inject(ReCaptchaV3Service);

  recaptchaSiteKey = environment.recaptchaSiteKey;

  //Form control
  authForm = new FormGroup({
    email: new FormControl(null, [Validators.required, Validators.email]),
    password: new FormControl(null, [Validators.required]),
    recaptcha: new FormControl<string | null>(null, [Validators.required]),
  });

  // API call (Login)
  mutation = injectMutation(() => ({
    mutationFn: (loginData: { [key: string]: any }) =>
      this.authService.login(loginData),
    onSuccess: ({ id, ...others }) => {
      // Invalidate and refetch by using the client directly

      // Invalidate and refetch by using the client directly

      this.store.dispatch(clearToastAction());

      const role = (others?.roles || [])?.[0];

      if (['Admin', 'Administrator'].includes(role)) {
        this.authForm.reset();
        this.authService.setUserData(others);
      }

      // Check if there's a saved redirect URL
      const redirectUrl = this.redirectService.getAndClearRedirectUrl();

      if (redirectUrl) {
        // If there's a saved URL, navigate to it
        this.router.navigateByUrl(redirectUrl);
        return;
      }

      // Otherwise, use default role-based navigation
      switch (role) {
        case 'Governor':
          this.router.navigate(['/governor']);
          break;
        case 'SuperAdmin':
        case 'SuperAdminViewOnly': {
          this.router.navigate(['/superadmin']);
          break;
        }
        case 'Admin':
        case 'Administrator':
          this.router.navigate(['/admin']);
          break;
        case 'Expert System':
          this.router.navigate(['/tsp']);
          break;

        default:
          this.router.navigate(['/']);
          break;
      }
      //OR use the queryClient that is injected into the component
      //this.queryClient.invalidateQueries({ queryKey: ['todos'] })
    },
    onError: (error: any) => {
      this.store.dispatch(
        errorToastAction({
          autohide: true,
          delay: 7000,
          placement: 'top-end',
          message:
            error?.error?.message ||
            'An error occurred while logging in. Try again shortly',
        }),
      );
    },
  }));

  onSubmit() {
    if (
      this.authForm.get('email')?.invalid ||
      this.authForm.get('password')?.invalid
    ) {
      this.authForm.get('email')?.markAsTouched();
      this.authForm.get('password')?.markAsTouched();
      return;
    }

    this.store.dispatch(
      loadingToastAction({
        autohide: false,
        delay: 7000,
        placement: 'top-end',
        message: 'Verifying security check. Please wait...',
      }),
    );

    this.reCaptchaV3Service
      .executeAsPromise(this.recaptchaSiteKey, 'login', {
        useGlobalDomain: false,
      })
      .then((token) => {
        this.authForm.patchValue({ recaptcha: token });
        this.store.dispatch(
          loadingToastAction({
            autohide: false,
            delay: 7000,
            placement: 'top-end',
            message: 'Logging in. Please wait...',
          }),
        );
        this.mutation.mutate(this.authForm.value);
      })
      .catch((error) => {
        console.error('reCAPTCHA error:', error);
        this.store.dispatch(
          errorToastAction({
            autohide: true,
            delay: 7000,
            placement: 'top-end',
            message: 'reCAPTCHA verification failed. Please try again.',
          }),
        );
      });
  }
}
